/**
 * Recruitment Service
 *
 * Comprehensive CRUD operations for recruitment system
 * Tables: recruits, contract_templates, contract_instances,
 *         recruitment_invitations, recruit_status_history
 */

import { supabase } from '@/utils/supabase';
import { createDefaultContractTemplate } from '@/components/recruitment/contract-seed-template';
import { createComponentLogger } from '@/utils/logger';
import type {
  Recruit,
  RecruitInsert,
  RecruitUpdate,
  RecruitWithRelations,
  ContractTemplate,
  ContractTemplateInsert,
  ContractTemplateUpdate,
  ContractInstance,
  ContractInstanceInsert,
  ContractInstanceUpdate,
  ContractInstanceWithRelations,
  RecruitmentInvitation,
  RecruitmentInvitationInsert,
  RecruitmentInvitationUpdate,
  RecruitmentInvitationWithRelations,
  RecruitStatusHistory,
  RecruitStatus
} from '@/types/database-recruitment';

const log = createComponentLogger('RecruitmentService', 'recruitment');

// ============================================================================
// RECRUITS - CRUD Operations
// ============================================================================

/**
 * Get all recruits for current organization with recruiter info
 */
export async function getRecruits(organizationId: string) {
  const { data, error } = await supabase
    .from('recruits')
    .select(`
      *,
      recruiter:users!recruiter_id(id, first_name, last_name, full_name, email)
    `)
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });

  if (error) {
throw error;
}
  return data as Recruit[];
}

/**
 * Get single recruit by ID with all relations
 */
export async function getRecruit(recruitId: string): Promise<RecruitWithRelations | null> {
  const { data, error } = await supabase
    .from('recruits')
    .select(`
      *,
      recruiter:users!recruiter_id(id, first_name, last_name, full_name, email),
      mentor:users!mentor_id(full_name, email),
      override_recipient:users!override_recipient_id(full_name, email),
      contract_instances(
        id,
        template_id,
        template_version,
        variable_data,
        sections,
        checkbox_states,
        status,
        sent_at,
        viewed_at,
        accepted_at,
        signature_date,
        signature_data_url,
        created_at,
        updated_at
      ),
      recruitment_invitations(
        id,
        token,
        expires_at,
        status,
        message,
        sent_at,
        viewed_at,
        cc_emails
      ),
      recruit_status_history(
        id,
        old_status,
        new_status,
        comment,
        changed_at,
        changed_by_user:users!changed_by(full_name, email)
      )
    `)
    .eq('id', recruitId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
return null;
} // Not found
    throw error;
  }

  return data as RecruitWithRelations;
}

/**
 * Get recruits by status with recruiter info
 */
export async function getRecruitsByStatus(organizationId: string, status: RecruitStatus) {
  const { data, error } = await supabase
    .from('recruits')
    .select(`
      *,
      recruiter:users!recruiter_id(id, first_name, last_name, full_name, email)
    `)
    .eq('organization_id', organizationId)
    .eq('status', status)
    .order('created_at', { ascending: false });

  if (error) {
throw error;
}
  return data as Recruit[];
}

/**
 * Search recruits by name or email with recruiter info
 */
export async function searchRecruits(organizationId: string, searchTerm: string) {
  const { data, error } = await supabase
    .from('recruits')
    .select(`
      *,
      recruiter:users!recruiter_id(id, first_name, last_name, full_name, email)
    `)
    .eq('organization_id', organizationId)
    .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false });

  if (error) {
throw error;
}
  return data as Recruit[];
}

/**
 * Create new recruit
 */
export async function createRecruit(recruit: RecruitInsert, userId: string) {
  const { data, error} = await supabase
    .from('recruits')
    .insert({
      ...recruit,
      created_by: userId,
      updated_by: userId
    })
    .select()
    .single();

  if (error) {
throw error;
}
  return data as Recruit;
}

/**
 * Update recruit
 */
export async function updateRecruit(
  recruitId: string,
  updates: RecruitUpdate,
  userId: string
) {
  const { data, error } = await supabase
    .from('recruits')
    .update({
      ...updates,
      updated_by: userId
    })
    .eq('id', recruitId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as Recruit;
}

/**
 * Update recruit status (triggers status history logging)
 */
export async function updateRecruitStatus(
  recruitId: string,
  newStatus: RecruitStatus,
  comment: string | null,
  userId: string
) {
  const { data, error } = await supabase
    .from('recruits')
    .update({
      status: newStatus,
      status_comment: comment,
      status_changed_at: new Date().toISOString(),
      status_changed_by: userId,
      updated_by: userId
    })
    .eq('id', recruitId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as Recruit;
}

/**
 * Delete recruit (hard delete with CASCADE)
 */
export async function deleteRecruit(recruitId: string) {
  const { error } = await supabase
    .from('recruits')
    .delete()
    .eq('id', recruitId);

  if (error) {
throw error;
}
}

// ============================================================================
// CONTRACT TEMPLATES - CRUD Operations
// ============================================================================

/**
 * Get active contract template for organization
 */
export async function getActiveContractTemplate(organizationId: string) {
  const { data, error } = await supabase
    .from('contract_templates')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('version', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
return null;
} // Not found
    throw error;
  }

  return data as ContractTemplate;
}

/**
 * Get all contract templates for organization
 */
export async function getContractTemplates(organizationId: string) {
  const { data, error } = await supabase
    .from('contract_templates')
    .select('*')
    .eq('organization_id', organizationId)
    .order('version', { ascending: false });

  if (error) {
throw error;
}
  return data as ContractTemplate[];
}

/**
 * Get contract template by ID
 */
export async function getContractTemplate(templateId: string) {
  const { data, error } = await supabase
    .from('contract_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
return null;
}
    throw error;
  }

  return data as ContractTemplate;
}

/**
 * Create new contract template
 */
export async function createContractTemplate(
  template: ContractTemplateInsert,
  userId: string
) {
  const { data, error } = await supabase
    .from('contract_templates')
    .insert({
      ...template,
      created_by: userId,
      updated_by: userId
    })
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractTemplate;
}

/**
 * Update contract template
 */
export async function updateContractTemplate(
  templateId: string,
  updates: ContractTemplateUpdate,
  userId: string
) {
  const { data, error } = await supabase
    .from('contract_templates')
    .update({
      ...updates,
      updated_by: userId
    })
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractTemplate;
}

/**
 * Publish contract template
 */
export async function publishContractTemplate(templateId: string, userId: string) {
  const { data, error } = await supabase
    .from('contract_templates')
    .update({
      published_at: new Date().toISOString(),
      published_by: userId,
      updated_by: userId
    })
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractTemplate;
}

/**
 * Deactivate contract template
 */
export async function deactivateContractTemplate(templateId: string, userId: string) {
  const { data, error } = await supabase
    .from('contract_templates')
    .update({
      is_active: false,
      updated_by: userId
    })
    .eq('id', templateId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractTemplate;
}

// ============================================================================
// CONTRACT INSTANCES - CRUD Operations
// ============================================================================

/**
 * Get contract instance for recruit
 */
export async function getContractInstance(recruitId: string): Promise<ContractInstance | null> {
  const { data, error } = await supabase
    .from('contract_instances')
    .select('*')
    .eq('recruit_id', recruitId)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
return null;
}
    throw error;
  }

  return data as ContractInstance;
}

/**
 * Get contract instance by ID with relations
 */
export async function getContractInstanceWithRelations(
  instanceId: string
): Promise<ContractInstanceWithRelations | null> {
  const { data, error } = await supabase
    .from('contract_instances')
    .select(`
      *,
      recruit:recruits(*),
      template:contract_templates(id, name, version)
    `)
    .eq('id', instanceId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
return null;
}
    throw error;
  }

  return data as ContractInstanceWithRelations;
}

/**
 * Create contract instance for recruit
 */
export async function createContractInstance(
  instance: ContractInstanceInsert,
  userId: string
) {
  const { data, error } = await supabase
    .from('contract_instances')
    .insert({
      ...instance,
      created_by: userId,
      updated_by: userId
    })
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

/**
 * Update contract instance
 */
export async function updateContractInstance(
  instanceId: string,
  updates: ContractInstanceUpdate,
  userId: string
) {
  const { data, error } = await supabase
    .from('contract_instances')
    .update({
      ...updates,
      updated_by: userId
    })
    .eq('id', instanceId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

/**
 * Update contract instance checkbox states
 */
export async function updateCheckboxStates(
  instanceId: string,
  checkboxStates: Record<string, boolean>,
  userId: string
) {
  const { data, error } = await supabase
    .from('contract_instances')
    .update({
      checkbox_states: checkboxStates,
      updated_by: userId
    })
    .eq('id', instanceId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

/**
 * Mark contract as sent
 */
export async function markContractAsSent(instanceId: string, userId: string) {
  const { data, error } = await supabase
    .from('contract_instances')
    .update({
      status: 'sent' as const,
      sent_at: new Date().toISOString(),
      updated_by: userId
    })
    .eq('id', instanceId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

/**
 * Mark contract as viewed
 */
export async function markContractAsViewed(instanceId: string) {
  const { data, error } = await supabase
    .from('contract_instances')
    .update({
      status: 'viewed' as const,
      viewed_at: new Date().toISOString()
    })
    .eq('id', instanceId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

/**
 * Mark contract as accepted
 */
export async function markContractAsAccepted(instanceId: string) {
  const { data, error } = await supabase
    .from('contract_instances')
    .update({
      status: 'accepted' as const,
      accepted_at: new Date().toISOString()
    })
    .eq('id', instanceId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

/**
 * Mark contract as signed
 */
export async function markContractAsSigned(
  instanceId: string,
  signatureDataUrl: string,
  signatureDate: string
) {
  const { data, error } = await supabase
    .from('contract_instances')
    .update({
      status: 'signed' as const,
      signature_data_url: signatureDataUrl,
      signature_date: signatureDate
    })
    .eq('id', instanceId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as ContractInstance;
}

// ============================================================================
// CONTRACT TEMPLATE AUTO-REGENERATION
// ============================================================================

/**
 * Get the latest template version available
 * Returns the MAXIMUM of database version and code template version
 * This ensures code template updates are always picked up
 */
export async function getLatestTemplateVersion(): Promise<number> {
  // Get code template version
  const codeTemplate = createDefaultContractTemplate();
  const codeVersion = codeTemplate.version;

  // Check database for active template
  const { data: dbTemplate, error } = await supabase
    .from('contract_templates')
    .select('version')
    .eq('is_active', true)
    .order('version', { ascending: false })
    .limit(1)
    .single();

  const dbVersion = (!error && dbTemplate) ? dbTemplate.version : 0;

  // Return the higher version (ensures code updates are picked up)
  return Math.max(codeVersion, dbVersion);
}

/**
 * Get the latest template (sections and version)
 * Used for regenerating contract instances
 * Returns whichever has higher version: database or code template
 */
export async function getLatestTemplate(): Promise<{
  version: number;
  sections: unknown[];
  id: string | null;  // null when using code template (no DB record)
}> {
  // Get code template
  const codeTemplate = createDefaultContractTemplate();

  // Check database for active template
  const { data: dbTemplate, error } = await supabase
    .from('contract_templates')
    .select('id, version, sections')
    .eq('is_active', true)
    .order('version', { ascending: false })
    .limit(1)
    .single();

  // If database has higher or equal version, use it
  if (!error && dbTemplate && dbTemplate.version >= codeTemplate.version) {
    return {
      id: dbTemplate.id,
      version: dbTemplate.version,
      sections: dbTemplate.sections as unknown[]
    };
  }

  // Code template has higher version - use it
  // Return null for id since code template doesn't have a DB record
  log.info('Using code template (higher version)', {
    codeVersion: codeTemplate.version,
    dbVersion: dbTemplate?.version ?? 0
  });

  return {
    id: null,  // No database record for code template
    version: codeTemplate.version,
    sections: codeTemplate.sections
  };
}

/**
 * Regenerate an unsigned contract instance from the latest template
 *
 * Only regenerates if:
 * - Contract status is 'draft'
 * - LO has NOT signed (has_lo_signature is false/null)
 *
 * Preserves:
 * - variable_data (recruit's personal info, compensation rates)
 * - checkbox_states (acknowledgment checkboxes)
 *
 * Updates:
 * - sections (from new template)
 * - template_version
 * - template_id (if changed)
 */
export async function regenerateContractFromTemplate(
  instanceId: string
): Promise<ContractInstance | null> {
  const timer = log.startTimer('regenerate_contract');

  try {
    // Fetch current instance
    const { data: instance, error: fetchError } = await supabase
      .from('contract_instances')
      .select('*')
      .eq('id', instanceId)
      .single();

    if (fetchError || !instance) {
      log.error('Failed to fetch instance for regeneration', { instanceId }, fetchError as Error);
      log.endTimer(timer, { success: false });
      return null;
    }

    // Safety check: only regenerate unsigned, non-voided contracts
    // Allows: draft, sent, viewed, accepted (all unsigned states before LO signs)
    // Blocks: recruit_signed, signed, voided (irreversible states)
    if (instance.has_lo_signature === true || instance.status === 'voided') {
      log.info('Skipping regeneration - contract is signed or voided', {
        instanceId,
        status: instance.status,
        hasLoSignature: instance.has_lo_signature
      });
      log.endTimer(timer, { success: false, reason: 'already_signed_or_voided' });
      return instance as ContractInstance;
    }

    // Get latest template
    const latestTemplate = await getLatestTemplate();

    // Check if regeneration is needed
    if (instance.template_version >= latestTemplate.version) {
      log.info('Template already up to date', {
        instanceId,
        currentVersion: instance.template_version,
        latestVersion: latestTemplate.version
      });
      log.endTimer(timer, { success: true, reason: 'already_current' });
      return instance as ContractInstance;
    }

    // Regenerate: update sections and version
    // Only update template_id if we have a valid database template (not code template)
    const updateData: Record<string, unknown> = {
      sections: latestTemplate.sections,
      template_version: latestTemplate.version,
      updated_at: new Date().toISOString()
    };

    // Only set template_id if it's a valid UUID (from database)
    if (latestTemplate.id !== null) {
      updateData.template_id = latestTemplate.id;
    }

    const { data: updated, error: updateError } = await supabase
      .from('contract_instances')
      .update(updateData)
      .eq('id', instanceId)
      .select()
      .single();

    if (updateError) {
      log.error('Failed to regenerate contract', { instanceId }, updateError);
      log.endTimer(timer, { success: false });
      return null;
    }

    log.info('Contract regenerated from updated template', {
      instanceId,
      previousVersion: instance.template_version,
      newVersion: latestTemplate.version
    });

    log.endTimer(timer, { success: true });
    return updated as ContractInstance;

  } catch (error) {
    log.error('Unexpected error during regeneration', { instanceId }, error as Error);
    log.endTimer(timer, { success: false });
    return null;
  }
}

// ============================================================================
// RECRUITMENT INVITATIONS - CRUD Operations
// ============================================================================

/**
 * Generate secure random token
 */
function generateSecureToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Create recruitment invitation
 */
export async function createRecruitmentInvitation(
  invitation: Omit<RecruitmentInvitationInsert, 'token'>,
  userId: string
) {
  const token = generateSecureToken();

  const { data, error } = await supabase
    .from('recruitment_invitations')
    .insert({
      ...invitation,
      token,
      sent_by: userId
    })
    .select()
    .single();

  if (error) {
throw error;
}
  return data as RecruitmentInvitation;
}

/**
 * Get invitation by token
 */
export async function getInvitationByToken(
  token: string
): Promise<RecruitmentInvitationWithRelations | null> {
  const { data, error } = await supabase
    .from('recruitment_invitations')
    .select(`
      *,
      recruit:recruits(*),
      sent_by_user:users!sent_by(full_name, email)
    `)
    .eq('token', token)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
return null;
}
    throw error;
  }

  return data as RecruitmentInvitationWithRelations;
}

/**
 * Get invitations for recruit
 */
export async function getRecruitInvitations(recruitId: string) {
  const { data, error } = await supabase
    .from('recruitment_invitations')
    .select('*')
    .eq('recruit_id', recruitId)
    .order('sent_at', { ascending: false });

  if (error) {
throw error;
}
  return data as RecruitmentInvitation[];
}

/**
 * Mark invitation as viewed
 */
export async function markInvitationAsViewed(invitationId: string) {
  const { data, error } = await supabase
    .from('recruitment_invitations')
    .update({
      status: 'viewed' as const,
      viewed_at: new Date().toISOString()
    })
    .eq('id', invitationId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as RecruitmentInvitation;
}

/**
 * Update invitation status
 */
export async function updateInvitationStatus(
  invitationId: string,
  updates: RecruitmentInvitationUpdate
) {
  const { data, error } = await supabase
    .from('recruitment_invitations')
    .update(updates)
    .eq('id', invitationId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as RecruitmentInvitation;
}

/**
 * Revoke invitation
 */
export async function revokeInvitation(invitationId: string) {
  const { data, error } = await supabase
    .from('recruitment_invitations')
    .update({
      status: 'revoked' as const
    })
    .eq('id', invitationId)
    .select()
    .single();

  if (error) {
throw error;
}
  return data as RecruitmentInvitation;
}

/**
 * Check if invitation is valid (not expired, not revoked)
 */
export async function isInvitationValid(token: string): Promise<boolean> {
  const invitation = await getInvitationByToken(token);

  if (!invitation) {
return false;
}
  if (invitation.status === 'revoked' || invitation.status === 'expired') {
return false;
}

  const now = new Date();
  const expiresAt = new Date(invitation.expires_at);

  if (now > expiresAt) {
    // Mark as expired
    await updateInvitationStatus(invitation.id, { status: 'expired' });
    return false;
  }

  return true;
}

// ============================================================================
// STATUS HISTORY - Read Operations (Insert handled by trigger)
// ============================================================================

/**
 * Get status history for recruit
 */
export async function getRecruitStatusHistory(recruitId: string) {
  const { data, error } = await supabase
    .from('recruit_status_history')
    .select(`
      *,
      changed_by_user:users!changed_by(full_name, email)
    `)
    .eq('recruit_id', recruitId)
    .order('changed_at', { ascending: false });

  if (error) {
throw error;
}
  return data as RecruitStatusHistory[];
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get current user's organization ID
 */
export async function getCurrentOrganizationId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
return null;
}

  const { data, error } = await supabase
    .from('users')
    .select('organization_id')
    .eq('id', user.id)
    .single();

  if (error) {
throw error;
}
  return data?.organization_id || null;
}

/**
 * Get current user ID
 */
export async function getCurrentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

// ============================================================================
// ID DOCUMENT APPROVAL/REJECTION
// ============================================================================

/**
 * Validate ID rejection reason
 * Must be between 20-500 characters
 */
export function validateIdRejectionReason(reason: string): {
  valid: boolean;
  message?: string;
} {
  const trimmed = reason.trim();
  if (trimmed.length < 20) {
    return { valid: false, message: `${20 - trimmed.length} more characters needed` };
  }
  if (trimmed.length > 500) {
    return { valid: false, message: 'Reason too long (max 500 characters)' };
  }
  return { valid: true };
}

/**
 * Approve a single ID document (Primary or Secondary)
 * When both IDs are approved, also sets id_validated_at
 */
export async function approveRecruitId(
  recruitId: string,
  idType: 'primary' | 'secondary',
  userId: string
): Promise<{ success: boolean; bothApproved: boolean }> {
  const now = new Date().toISOString();

  // Build update object for specific ID
  const updateData: Record<string, any> = {
    updated_by: userId,
    updated_at: now
  };

  if (idType === 'primary') {
    updateData.id_primary_status = 'approved';
    updateData.id_primary_approved_at = now;
    updateData.id_primary_approved_by = userId;
    // Clear any previous rejection
    updateData.id_primary_rejected_at = null;
    updateData.id_primary_rejected_by = null;
    updateData.id_primary_rejection_reason = null;
  } else {
    updateData.id_secondary_status = 'approved';
    updateData.id_secondary_approved_at = now;
    updateData.id_secondary_approved_by = userId;
    // Clear any previous rejection
    updateData.id_secondary_rejected_at = null;
    updateData.id_secondary_rejected_by = null;
    updateData.id_secondary_rejection_reason = null;
  }

  // Update the recruit
  const { data, error } = await supabase
    .from('recruits')
    .update(updateData)
    .eq('id', recruitId)
    .select('id_primary_status, id_secondary_status')
    .single();

  if (error) {
throw error;
}

  // Check if both IDs are now approved
  const bothApproved =
    data.id_primary_status === 'approved' &&
    data.id_secondary_status === 'approved';

  // If both are approved, set the id_validated_at timestamp
  if (bothApproved) {
    const { error: validationError } = await supabase
      .from('recruits')
      .update({
        id_validated_at: now,
        id_validated_by: userId
      })
      .eq('id', recruitId);

    if (validationError) {
throw validationError;
}
  }

  return { success: true, bothApproved };
}

/**
 * Reject a single ID document (Primary or Secondary)
 * Deletes the files from storage and clears paths so recruit can re-upload
 * Clears id_validated_at to keep the onboarding task open
 */
export async function rejectRecruitId(
  recruitId: string,
  idType: 'primary' | 'secondary',
  userId: string,
  reason: string
): Promise<{ success: boolean }> {
  // Validate reason
  const validation = validateIdRejectionReason(reason);
  if (!validation.valid) {
    throw new Error(validation.message || 'Invalid rejection reason');
  }

  // Fetch current file paths to delete from storage
  const pathFields = idType === 'primary'
    ? 'id_primary_front_path, id_primary_back_path'
    : 'id_secondary_front_path, id_secondary_back_path';

  const { data: recruit, error: fetchError } = await supabase
    .from('recruits')
    .select(pathFields)
    .eq('id', recruitId)
    .maybeSingle();

  if (fetchError) {
    log.error('Failed to fetch recruit for ID rejection', { recruitId, error: fetchError });
    throw fetchError;
  }

  // Delete files from storage (non-blocking, don't fail if storage delete fails)
  if (recruit) {
    const filesToDelete: string[] = [];
    if (idType === 'primary') {
      if (recruit.id_primary_front_path) {
filesToDelete.push(recruit.id_primary_front_path);
}
      if (recruit.id_primary_back_path) {
filesToDelete.push(recruit.id_primary_back_path);
}
    } else {
      if (recruit.id_secondary_front_path) {
filesToDelete.push(recruit.id_secondary_front_path);
}
      if (recruit.id_secondary_back_path) {
filesToDelete.push(recruit.id_secondary_back_path);
}
    }

    if (filesToDelete.length > 0) {
      const { error: deleteError } = await supabase.storage
        .from('recruit-identifications')
        .remove(filesToDelete);

      if (deleteError) {
        log.warn('Failed to delete ID files from storage', { recruitId, files: filesToDelete, error: deleteError });
        // Continue with rejection even if storage delete fails
      } else {
        log.info('Deleted rejected ID files from storage', { recruitId, files: filesToDelete });
      }
    }
  }

  const now = new Date().toISOString();

  // Build update object for specific ID
  const updateData: Record<string, any> = {
    updated_by: userId,
    updated_at: now,
    // Clear validated_at so the task stays open
    id_validated_at: null,
    id_validated_by: null
  };

  if (idType === 'primary') {
    updateData.id_primary_status = 'rejected';
    updateData.id_primary_rejected_at = now;
    updateData.id_primary_rejected_by = userId;
    updateData.id_primary_rejection_reason = reason.trim();
    // Clear any previous approval
    updateData.id_primary_approved_at = null;
    updateData.id_primary_approved_by = null;
    // Clear file paths so recruit can re-upload
    updateData.id_primary_front_path = null;
    updateData.id_primary_back_path = null;
    updateData.id_primary_type = null;
  } else {
    updateData.id_secondary_status = 'rejected';
    updateData.id_secondary_rejected_at = now;
    updateData.id_secondary_rejected_by = userId;
    updateData.id_secondary_rejection_reason = reason.trim();
    // Clear any previous approval
    updateData.id_secondary_approved_at = null;
    updateData.id_secondary_approved_by = null;
    // Clear file paths so recruit can re-upload
    updateData.id_secondary_front_path = null;
    updateData.id_secondary_back_path = null;
    updateData.id_secondary_type = null;
  }

  const { error } = await supabase
    .from('recruits')
    .update(updateData)
    .eq('id', recruitId);

  if (error) {
throw error;
}

  // Send email notification to recruit (non-blocking)
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session?.access_token) {
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-recruit-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({
          recruitId,
          notificationType: 'recruit_id_rejected',
          data: {
            idType,
            rejectionReason: reason.trim(),
            uploadPortalUrl: `${window.location.origin}/secureuploads`
          }
        })
      }).catch(err => {
        log.warn('Failed to send ID rejection notification email', { recruitId, error: err });
      });
    }
  } catch (notifyError) {
    // Don't fail the rejection if notification fails
    log.warn('Failed to send ID rejection notification', { recruitId, error: notifyError });
  }

  return { success: true };
}

/**
 * Get ID document status for a recruit
 * Used by the upload portal to check rejection status
 */
export async function getRecruitIdStatus(recruitId: string): Promise<{
  primaryStatus: 'pending' | 'approved' | 'rejected' | null;
  primaryRejectionReason: string | null;
  secondaryStatus: 'pending' | 'approved' | 'rejected' | null;
  secondaryRejectionReason: string | null;
}> {
  const { data, error } = await supabase
    .from('recruits')
    .select(`
      id_primary_status,
      id_primary_rejection_reason,
      id_secondary_status,
      id_secondary_rejection_reason
    `)
    .eq('id', recruitId)
    .single();

  if (error) {
throw error;
}

  return {
    primaryStatus: data.id_primary_status,
    primaryRejectionReason: data.id_primary_rejection_reason,
    secondaryStatus: data.id_secondary_status,
    secondaryRejectionReason: data.id_secondary_rejection_reason
  };
}
